import { useState, useCallback } from 'react';
import { AxiosResponse } from 'axios';
import { useToast } from "../../../hooks/use-toast";
import { ValidationHistoryAxiosInstance } from "../../../Network/ValidationHistory";
import { FilterState, ApiResponse, ClaimSubmission } from "../../../interface/ValidationHistory";
import styles from "./ValidationHistory.module.css";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso: string) => {
  if (!iso) return "N/A";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const isResultFailed = (status: string) =>
  status?.toUpperCase() === "FAILED" || status?.toUpperCase() === "FAIL";

const claimOverallStatus = (claim: ClaimSubmission): "PASSED" | "FAILED" | "WARNING" => {
  const diagFails = (claim.diagnoses || []).some(d => (d.results || []).some(r => isResultFailed(r.Status)));
  const actFails = (claim.activities || []).some(a => (a.results || []).some(r => isResultFailed(r.Status)));
  const firstFails = (claim.firstModuleChecks || []).some(c => c['Response-Status']?.toLowerCase() === 'failed');
  
  if (diagFails || actFails || firstFails) return "FAILED";
  if (claim.firstModuleChecks && claim.firstModuleChecks.length > 0) return "WARNING";
  return "PASSED";
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const up = status?.toUpperCase();
  let badgeCls = styles.badgePassed;
  let label = "PASSED";
  
  if (up === "FAILED" || up === "FAIL" || up === "HAS ERRORS / WARNINGS") {
    badgeCls = styles.badgeFailed;
    label = "FAILED";
  } else if (up === "EDIT" || up === "WARNING") {
    badgeCls = styles.badgeWarning;
    label = "WARNING";
  }
  return (
    <span className={`${styles.badge} ${badgeCls}`} data-testid="ClaimValidationHistory-status-badge">
      {label}
    </span>
  );
};

const SectionTitle: React.FC<{ icon: string; label: string; count?: number }> = ({ icon, label, count }) => (
  <h4 className={styles.sectionTitle}>
    <i className={`fas ${icon}`} style={{ color: '#011934' }}></i>
    {label}
    {count !== undefined && (
      <span className={styles.sectionCountBadge}>{count}</span>
    )}
  </h4>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const ClaimValidationHistory: React.FC = () => {
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);
  const [historyData, setHistoryData] = useState<ClaimSubmission[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [filters, setFilters] = useState<FilterState>({
    claimId: '',
    patientName: '',
    patientIdentifierValue: '',
    claimDate: '',
  });

  // Server-side pagination states based on backend DTO
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  // ── Data fetching (Server-side) ───────────────────────────────────────────

  const fetchHistory = useCallback(async (page = 1): Promise<void> => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('limit', String(itemsPerPage));
      
      if (filters.claimId) params.append('claimId', filters.claimId);
      if (filters.patientName) params.append('patientName', filters.patientName);
      if (filters.patientIdentifierValue) params.append('patientIdentifierValue', filters.patientIdentifierValue);
      if (filters.claimDate) params.append('date', filters.claimDate);

      const response: AxiosResponse<ApiResponse> = await ValidationHistoryAxiosInstance.get(
        `claim-validation?${params.toString()}`
      );
      
      const raw = response.data.data;

      if (raw && raw.items) {
        setHistoryData(raw.items);
        setTotalRecords(raw.total || 0);
        setTotalPages(raw.totalPages || 1);
        setCurrentPage(page);
      } else {
        setHistoryData([]);
        setTotalRecords(0);
        setTotalPages(1);
      }
  
      setExpandedRows(new Set());
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.error?.message || error.response?.data?.message || "Failed to fetch validation history",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [filters, toast]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    fetchHistory(1);
  };

  const handlePageChange = (page: number) => {
    fetchHistory(page);
  };

  const clearFilters = () => {
    setFilters({ claimId: '', patientName: '', patientIdentifierValue: '', claimDate: '' });
    setHistoryData([]);
    setTotalRecords(0);
    setTotalPages(1);
    setCurrentPage(1);
    setExpandedRows(new Set());
  };

  const toggleRowExpansion = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleDeleteClick = async () => {
    try {
      setClearing(true);
      setActionError(null);

      const response = await ValidationHistoryAxiosInstance.delete('claim-validation');
      
      if (response.data.success) {
        toast({
          title: "History Cleared",
          description: "All validation history records cleared permanently.",
        });
        setHistoryData([]);
        setTotalRecords(0);
        setTotalPages(1);
        setCurrentPage(1);
        setExpandedRows(new Set());
      }
    } catch (error: any) {
      const errorMessage = 
        error.response?.data?.error?.message || 
        error.response?.data?.message || 
        "Failed to clear history";

      setActionError(errorMessage);
      setShowClearConfirm(true); 
    } finally {
      setClearing(false);
    }
  };

  const confirmClearHistory = async () => {
    try {
      setClearing(true);
      const response = await ValidationHistoryAxiosInstance.delete('claim-validation');
      if (response.data.success) {
        toast({
          title: "History Cleared",
          description: "All validation history records cleared permanently.",
        });
        setHistoryData([]);
        setTotalRecords(0);
        setTotalPages(1);
        setCurrentPage(1);
        setExpandedRows(new Set());
        setShowClearConfirm(false);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || error.response?.data?.message || "Failed to clear history";
      setActionError(errorMessage);
    } finally {
      setClearing(false);
    }
  };

  const startIndex = (currentPage - 1) * itemsPerPage;

  return (
    <div className={styles.pageWrapper} data-testid="ClaimValidationHistory-page">
      <div className={styles.container} data-testid="ClaimValidationHistory-page-container">

        {/* ── Filter Card ── */}
        <div className={styles.filterCard} data-testid="ClaimValidationHistory-filter-card">
          <div className={styles.filterCardHeader}>
            <div className={styles.filterCardHeaderIcon}>
              <i className="fas fa-filter text-white text-sm"></i>
            </div>
            <div>
              <h2 className={styles.filterCardTitle} data-testid="ClaimValidationHistory-title">Claims Validation History</h2>
              <p className={styles.filterCardSubtitle}>Track and review rules engine validation output for all submissions</p>
            </div>
          </div>

          <div className={styles.filterCardBody} data-testid="ClaimValidationHistory-filter-body">
            <div className={styles.filterGrid} style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
              {/* Claim ID */}
              <div data-testid="ClaimValidationHistory-field-claimId">
                <label htmlFor="claimId" className={styles.filterLabel}>
                  Claim ID
                </label>
                <input
                  data-testid="ClaimValidationHistory-input-claimId"
                  type="text"
                  id="claimId"
                  name="claimId"
                  value={filters.claimId}
                  onChange={handleFilterChange}
                  placeholder="Enter claim ID"
                  className={styles.filterInput}
                />
              </div>

              {/* Patient Name */}
              <div data-testid="ClaimValidationHistory-field-patientName">
                <label htmlFor="patientName" className={styles.filterLabel}>
                  Patient Name
                </label>
                <input
                  data-testid="ClaimValidationHistory-input-patientName"
                  type="text"
                  id="patientName"
                  name="patientName"
                  value={filters.patientName}
                  onChange={handleFilterChange}
                  placeholder="Enter patient name"
                  className={styles.filterInput}
                />
              </div>

              {/* Identifier value */}
              <div data-testid="ClaimValidationHistory-field-patientIdentifierValue">
                <label htmlFor="patientIdentifierValue" className={styles.filterLabel}>
                  Identifier value
                </label>
                <input
                  data-testid="ClaimValidationHistory-input-patientIdentifierValue"
                  type="text"
                  id="patientIdentifierValue"
                  name="patientIdentifierValue"
                  value={filters.patientIdentifierValue}
                  onChange={handleFilterChange}
                  placeholder="Identifier value"
                  className={styles.filterInput}
                />
              </div>

              {/* Claim Date */}
              <div data-testid="ClaimValidationHistory-field-claimDate">
                <label htmlFor="claimDate" className={styles.filterLabel}>
                  Claim Date
                </label>
                <input
                  data-testid="ClaimValidationHistory-input-claimDate"
                  type="date"
                  id="claimDate"
                  name="claimDate"
                  value={filters.claimDate}
                  onChange={handleFilterChange}
                  className={styles.filterInput}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className={styles.filterActions} data-testid="ClaimValidationHistory-filter-actions">
              <button
                data-testid="ClaimValidationHistory-btn-search"
                onClick={handleSearch}
                disabled={loading}
                className={styles.btnPrimary}
              >
                {loading ? (
                  <><i className="fas fa-spinner fa-spin"></i> Searching...</>
                ) : (
                  <><i className="fas fa-search"></i> Search</>
                )}
              </button>

              <button
                data-testid="ClaimValidationHistory-btn-reset-filters"
                onClick={clearFilters}
                disabled={loading}
                className={styles.btnSecondary}
              >
                <i className="fas fa-redo"></i> Reset Filters
              </button>

              <button
                data-testid="ClaimValidationHistory-btn-delete-all"
                onClick={handleDeleteClick}
                disabled={loading || clearing}
                className={styles.btnDanger}
              >
                {clearing ? <><i className="fas fa-spinner fa-spin"></i> Checking...</> : <><i className="fas fa-trash"></i> Delete All History</>}
              </button>
            </div>
          </div>
        </div>

        {/* ── Stats Bar (Total Records Only) ── */}
        {historyData && historyData.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '1.5rem' }} data-testid="ClaimValidationHistory-stats-grid">
            <div className={styles.statCard} style={{ maxWidth: '280px', width: '100%' }} data-testid="ClaimValidationHistory-stat-total-records">
              <div>
                <p className={styles.statLabel}>Total Records</p>
                <p className={styles.statValue}>{totalRecords}</p>
              </div>
              <div className={styles.statIcon} style={{ backgroundColor: '#3b82f6' }}>
                <i className="fas fa-database text-white text-sm"></i>
              </div>
            </div>
          </div>
        )}

        {/* ── Clean Records List ── */}
        {loading ? (
          <div className={styles.stateBox} data-testid="ClaimValidationHistory-loading-state">
            <i className="fas fa-spinner fa-spin text-4xl mb-4" style={{ color: '#011934' }}></i>
            <p className="text-gray-500 font-medium">Loading validation history...</p>
          </div>
        ) : historyData.length > 0 ? (
          <div className={styles.claimList} data-testid="ClaimValidationHistory-claims-list">
            {historyData.map((claim, index) => {
              const actualIndex = startIndex + index;
              const isExpanded = expandedRows.has(claim.id);
              const status = claimOverallStatus(claim);

              const headerBg =
                status === "FAILED" ? 'linear-gradient(to right, #fff5f5, #fff)' :
                status === "WARNING" ? 'linear-gradient(to right, #fffbeb, #fff)' :
                'linear-gradient(to right, #f0fdf4, #fff)';

              return (
                <div
                  key={claim.id}
                  className={styles.claimCard}
                  style={{ borderLeft: `6px solid ${status === "FAILED" ? '#dc2626' : status === "WARNING" ? '#d97706' : '#16a34a'}` }}
                  data-testid={`ClaimValidationHistory-claim-card-${actualIndex}`}
                >
                  {/* Card Header */}
                  <div
                    className={styles.claimCardHeader}
                    style={{ background: headerBg }}
                  >
                    <div className={styles.claimCardHeaderLeft}>
                      <div className={styles.claimIndex}>
                        #{actualIndex + 1}
                      </div>
                      <div>
                        <h3 className={styles.claimTitle}>
                          <i className="fas fa-file-invoice" style={{ color: '#011934' }}></i>
                          Claim Reference ID: {claim.claimId || 'N/A'}
                        </h3>
                        <p className={styles.claimMeta}>
                          <i className="fas fa-user text-gray-400"></i>
                          Patient: <span className="font-semibold text-slate-700">{claim.patientName || 'N/A'}</span>
                          <span className={styles.metaDivider}>|</span>
                          <i className="fas fa-id-card text-gray-400"></i>
                          Identifier value: <span className="font-semibold text-slate-700">{claim.patientIdentifierValue || 'N/A'}</span>
                        </p>
                      </div>
                    </div>

                    <div className={styles.claimCardHeaderRight}>
                      <StatusBadge status={status} />
                      <button
                        data-testid={`ClaimValidationHistory-btn-expand-${actualIndex}`}
                        onClick={() => toggleRowExpansion(claim.id)}
                        className={styles.expandBtn}
                      >
                        <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-gray-500 text-sm`}></i>
                      </button>
                    </div>
                  </div>

                  {/* Card Body – Overview */}
                  <div className={styles.claimCardBody}>
                    <div className={styles.overviewGrid}>
                      <div>
                        <p className={styles.overviewFieldLabel}>Claim Date</p>
                        <p className={styles.overviewFieldValue}>{formatDate(claim.claimDate)}</p>
                      </div>
                      <div>
                        <p className={styles.overviewFieldLabel}>Diagnoses Count</p>
                        <p className={styles.overviewFieldValue}>
                          <span className={styles.countBadge}>{claim.diagnoses?.length || 0} Codes</span>
                        </p>
                      </div>
                      <div>
                        <p className={styles.overviewFieldLabel}>Activities Count</p>
                        <p className={styles.overviewFieldValue}>
                          <span className={styles.countBadge}>{claim.activities?.length || 0} Activities</span>
                        </p>
                      </div>
                      <div>
                        <p className={styles.overviewFieldLabel}>Network</p>
                        <p className={styles.overviewFieldValue}>{claim.networkName || 'N/A'}</p>
                      </div>
                      <div>
                        <p className={styles.overviewFieldLabel}>Policy</p>
                        <p className={styles.overviewFieldValue}>{claim.policyName || 'N/A'}</p>
                      </div>
                      <div>
                        <p className={styles.overviewFieldLabel}>Gender</p>
                        <p className={styles.overviewFieldValue}>{claim.patientGender || 'N/A'}</p>
                      </div>
                    </div>

                    {/* ── Expanded Details Area ── */}
                    {isExpanded && (
                      <div className={styles.expandedDetails} data-testid={`ClaimValidationHistory-expanded-content-${actualIndex}`}>

                        {/* First Module Checks (Bayan) */}
                        {claim.firstModuleChecks && claim.firstModuleChecks.length > 0 && (
                          <div className="space-y-3">
                            <SectionTitle icon="fa-shield-alt" label="Integration & Connection Checks (1st Module)" count={claim.firstModuleChecks.length} />
                            {claim.firstModuleChecks.map((check, idx) => {
                              const failed = check['Response-Status']?.toLowerCase() === 'failed';
                              return (
                                <div
                                  key={idx}
                                  className={`${styles.checkItem} ${failed ? styles.checkItemFailed : styles.checkItemWarning}`}
                                >
                                  <div className={styles.checkItemHeader}>
                                    <span className={styles.checkItemTitle}>
                                      <i className={`fas ${failed ? 'fa-times-circle text-red-500' : 'fa-exclamation-circle text-amber-500'}`}></i>
                                      {check.type || 'BAYAN_API_ERROR'}
                                    </span>
                                    <span className={styles.checkItemSource}>
                                      Source: {check.source || 'BAYAN (1st Module)'}
                                    </span>
                                  </div>
                                  <p className={`${styles.checkItemMessage} ${failed ? styles.checkItemMessageFailed : styles.checkItemMessageWarning}`}>{check.Message}</p>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Diagnoses Validation Results */}
                        {claim.diagnoses && claim.diagnoses.length > 0 && (
                          <div className="space-y-4">
                            <SectionTitle icon="fa-stethoscope" label="Diagnoses Validation Results" />
                            {claim.diagnoses.map(diag => (
                              <div key={diag.id} className={styles.subCard}>
                                <div className={styles.subCardHeader}>
                                  <div className={styles.subCardHeaderLeft}>
                                    <span className={styles.codeBadgeBlue}>
                                      {diag.medicalIcdCode?.code}
                                    </span>
                                    <span className={styles.subCardDescription}>{diag.medicalIcdCode?.description}</span>
                                  </div>
                                  <span className={styles.diagnosisTypeBadge}>
                                    {diag.diagnosisType}
                                  </span>
                                </div>

                                {diag.results && diag.results.length > 0 ? (
                                  <div className={styles.resultList}>
                                    {diag.results.map(r => (
                                      <div
                                        key={r.id}
                                        className={`${styles.resultRow} ${isResultFailed(r.Status) ? styles.resultRowFailed : styles.resultRowPassed}`}
                                      >
                                        <div>
                                          <p className={styles.resultRuleName}>{r.ruleName}</p>
                                          <p className={styles.resultMessage}>{r.Message}</p>
                                        </div>
                                        <span className={`${styles.resultStatusBadge} ${isResultFailed(r.Status) ? styles.resultStatusBadgeFailed : styles.resultStatusBadgePassed}`}>
                                          {r.Status}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className={styles.noIssues}>
                                    <i className="fas fa-check-circle"></i> No issues found for this ICD code.
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Activities & Procedures Validation */}
                        {claim.activities && claim.activities.length > 0 && (
                          <div className="space-y-4">
                            <SectionTitle icon="fa-procedures" label="Activities & Procedures Validation" />
                            {claim.activities.map(act => (
                              <div key={act.id} className={styles.subCard}>
                                <div className={styles.subCardHeader}>
                                  <div className={styles.subCardHeaderLeft}>
                                    <span className={styles.codeBadgeSlate}>
                                      {act.medicalActivityCode?.code}
                                    </span>
                                    <span className={styles.subCardDescription}>{act.medicalActivityCode?.description}</span>
                                  </div>
                                  <span className={styles.quantityLabel}>Qty: {act.quantity}</span>
                                </div>

                                {act.results && act.results.length > 0 ? (
                                  <div className={styles.resultList}>
                                    {act.results.map(r => (
                                      <div
                                        key={r.id}
                                        className={`${styles.resultRow} ${isResultFailed(r.Status) ? styles.resultRowFailed : styles.resultRowWarning}`}
                                      >
                                        <div>
                                          <p className={styles.resultRuleName}>{r.ruleName}</p>
                                          <p className={styles.resultMessage}>{r.Message}</p>
                                        </div>
                                        <span className={`${styles.resultStatusBadge} ${isResultFailed(r.Status) ? styles.resultStatusBadgeFailed : styles.resultStatusBadgeWarning}`}>
                                          {r.Status}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className={styles.noIssues}>
                                    <i className="fas fa-check-circle"></i> No issues found for this activity.
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Technical claim Details */}
                        <div className={styles.techDetailsBox}>
                          <SectionTitle icon="fa-info-circle" label="Technical Claim Details" />
                          <div className={styles.techDetailsGrid}>
                            <div><span className={styles.techDetailsKey}>Doctor License:</span> {claim.doctorLicenseNumber || 'N/A'}</div>
                            <div><span className={styles.techDetailsKey}>Speciality Code:</span> {claim.specialityCode || 'N/A'}</div>
                            <div><span className={styles.techDetailsKey}>Encounter Type:</span> {claim.encounterType ?? 'N/A'}</div>
                            <div><span className={styles.techDetailsKey}>TPA Business ID:</span> {claim.tpaBusinessId || 'N/A'}</div>
                            <div><span className={styles.techDetailsKey}>Payer Business ID:</span> {claim.payerBusinessId || 'N/A'}</div>
                            <div><span className={styles.techDetailsKey}>Policy Business ID:</span> {claim.policyBusinessId || 'N/A'}</div>
                          </div>
                        </div>

                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          !loading && (
            <div className={styles.stateBox} data-testid="ClaimValidationHistory-empty-state">
              <div className={styles.stateIconWrapper}>
                <i className="fas fa-search text-2xl text-gray-300"></i>
              </div>
              <h3 className={styles.stateTitle}>No History Found</h3>
              <p className={styles.stateSubtitle}>Use the search filters above to load the history records</p>
            </div>
          )
        )}

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div className={styles.pagination} data-testid="ClaimValidationHistory-pagination-container">
            <div className={styles.paginationInner}>
              <p className={styles.paginationInfo}>
                Showing <strong data-testid="ClaimValidationHistory-pagination-start">{startIndex + 1}</strong> to{' '}
                <strong data-testid="ClaimValidationHistory-pagination-end">{Math.min(startIndex + itemsPerPage, totalRecords)}</strong> of{' '}
                <strong data-testid="ClaimValidationHistory-pagination-total">{totalRecords}</strong> results
              </p>
              <div className={styles.paginationControls}>
                <button
                  data-testid="ClaimValidationHistory-btn-previous"
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={styles.pageBtn}
                >
                  Previous
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum = i + 1;
                  return (
                    <button
                      data-testid={`ClaimValidationHistory-btn-page-${pageNum}`}
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`${styles.pageNumberBtn} ${currentPage === pageNum ? styles.pageNumberBtnActive : ''}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  data-testid="ClaimValidationHistory-btn-next"
                  onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className={styles.pageBtn}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Error / Confirm Modal ── */}
      {showClearConfirm && (
        <div className={styles.modalOverlay} data-testid="ClaimValidationHistory-clear-modal-overlay">
          <div className={styles.modal} data-testid="ClaimValidationHistory-clear-modal-content">
            <h3 className={styles.modalTitle}>
              <i className="fas fa-exclamation-triangle text-red-500"></i> Clear All History
            </h3>

            {actionError ? (
              <>
                <div style={{ backgroundColor: '#fef2f2', border: '1px solid #f87171', color: '#b91c1c', padding: '12px 14px', borderRadius: '6px', margin: '15px 0', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <i className="fas fa-exclamation-circle" style={{ fontSize: '18px' }}></i>
                  <span>{actionError}</span>
                </div>
                <div className={styles.modalActions} style={{ justifyContent: 'flex-end' }}>
                  <button
                    data-testid="ClaimValidationHistory-modal-btn-cancel"
                    onClick={() => {
                      setShowClearConfirm(false);
                      setActionError(null);
                    }}
                    className={styles.modalBtnCancel}
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
           
              <>
                <p className={styles.modalBody}>
                  Are you sure you want to proceed? This will permanently delete all logs of claim validation history and this process cannot be undone.
                </p>
                <div className={styles.modalActions}>
                  <button
                    data-testid="ClaimValidationHistory-modal-btn-confirm"
                    onClick={confirmClearHistory}
                    disabled={clearing}
                    className={styles.modalBtnDanger}
                  >
                    {clearing ? 'Clearing...' : 'Yes, Delete All'}
                  </button>
                  <button
                    data-testid="ClaimValidationHistory-modal-btn-cancel"
                    onClick={() => {
                      setShowClearConfirm(false);
                      setActionError(null);
                    }}
                    disabled={clearing}
                    className={styles.modalBtnCancel}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}

          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimValidationHistory;